import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../services/api';

interface Destination {
  id?: number;
  name: string;
  country: string;
  region: string;
  price: number;
  description: string;
  budget: string;
  visa_free: boolean;
  image_url: string;
  is_active: boolean;
  sort_order: number;
}

const emptyDest: Destination = {
  name: '', country: '', region: '', price: 0, description: '',
  budget: 'mid-range', visa_free: false, image_url: '', is_active: true, sort_order: 0
};

export default function AdminDestinations() {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Destination | null>(null);
  const [form, setForm] = useState<Destination>(emptyDest);

  const fetchDestinations = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/destinations/all');
      setDestinations(data || []);
    } catch {
      setError('Failed to load destinations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDestinations(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyDest); setShowModal(true); };
  const openEdit = (d: Destination) => { setEditing(d); setForm({ ...d }); setShowModal(true); };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked
        : name === 'price' || name === 'sort_order' ? Number(value)
        : value
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.country || form.price <= 0) {
      setError('Name, country, and a valid price are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      if (editing?.id) {
        await apiFetch(`/destinations/${editing.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setSuccess('Destination updated!');
      } else {
        await apiFetch('/destinations', { method: 'POST', body: JSON.stringify(form) });
        setSuccess('Destination created!');
      }
      setShowModal(false);
      fetchDestinations();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to save destination.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this destination? This cannot be undone.')) return;
    try {
      await apiFetch(`/destinations/${id}`, { method: 'DELETE' });
      setDestinations(prev => prev.filter(d => d.id !== id));
      setSuccess('Destination deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to delete.'); }
  };

  const toggleActive = async (d: Destination) => {
    try {
      await apiFetch(`/destinations/${d.id}`, { method: 'PUT', body: JSON.stringify({ ...d, is_active: !d.is_active }) });
      setDestinations(prev => prev.map(x => x.id === d.id ? { ...x, is_active: !x.is_active } : x));
    } catch { setError('Failed to update status.'); }
  };

  return (
    <div className="p-8 min-h-screen animate-fade-in-up font-sans">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-extrabold text-slate-900">Destinations</h1>
          <p className="text-slate-500 mt-1">Manage popular destination cards shown to users</p>
        </div>
        <button onClick={openCreate} className="bg-logo-gold-600 hover:bg-logo-gold-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-logo-gold-600/30 flex items-center gap-2 transition-all">
          <i className="ri-add-line text-lg"></i> Add Destination
        </button>
      </div>

      {success && <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl font-medium">{success}</div>}
      {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl font-medium">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-gold-600"></div></div>
      ) : (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Destination</th>
                <th className="px-6 py-4">Region</th>
                <th className="px-6 py-4">Price (₦)</th>
                <th className="px-6 py-4">Budget</th>
                <th className="px-6 py-4">Visa-Free</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {destinations.map(d => (
                <tr key={d.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {d.image_url && <img src={d.image_url} alt={d.name} className="w-10 h-10 rounded-lg object-cover" />}
                      <div>
                        <p className="font-bold text-slate-900">{d.name}</p>
                        <p className="text-xs text-slate-400">{d.country}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 text-sm">{d.region}</td>
                  <td className="px-6 py-4 font-bold text-slate-900">₦{Number(d.price).toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize border ${
                      d.budget === 'luxury' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                      d.budget === 'mid-range' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-green-50 text-green-700 border-green-200'
                    }`}>{d.budget}</span>
                  </td>
                  <td className="px-6 py-4">
                    {d.visa_free ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs font-bold"><i className="ri-checkbox-circle-fill"></i> Yes</span>
                    ) : (
                      <span className="text-slate-400 text-xs font-medium">No</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button onClick={() => toggleActive(d)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${d.is_active ? 'bg-logo-gold-600' : 'bg-slate-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${d.is_active ? 'translate-x-6' : 'translate-x-1'}`}></span>
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(d)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"><i className="ri-edit-line text-lg"></i></button>
                      <button onClick={() => handleDelete(d.id!)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"><i className="ri-delete-bin-line text-lg"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
              {destinations.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-slate-400">No destinations yet. Click "Add Destination" to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-extrabold text-slate-900">{editing ? 'Edit Destination' : 'Add Destination'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400"><i className="ri-close-line text-xl"></i></button>
              </div>

              {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Ghana" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Country *</label>
                    <input name="country" value={form.country} onChange={handleChange} placeholder="e.g. Ghana" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Region</label>
                    <input name="region" value={form.region} onChange={handleChange} placeholder="e.g. West Africa" className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Price (₦) *</label>
                    <input name="price" type="number" value={form.price} onChange={handleChange} min={0} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                  <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Short description of the destination..." className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800 resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Image URL</label>
                  <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Budget Tier</label>
                    <select name="budget" value={form.budget} onChange={handleChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800">
                      <option value="budget">Budget</option>
                      <option value="mid-range">Mid-Range</option>
                      <option value="luxury">Luxury</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Sort Order</label>
                    <input name="sort_order" type="number" value={form.sort_order} onChange={handleChange} min={0} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="visa_free" checked={form.visa_free} onChange={handleChange} className="accent-logo-gold-500 w-4 h-4" />
                    <span className="text-sm font-medium text-slate-700">Visa-Free</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} className="accent-logo-gold-500 w-4 h-4" />
                    <span className="text-sm font-medium text-slate-700">Active (visible to users)</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-colors">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl bg-logo-gold-600 hover:bg-logo-gold-700 text-white font-bold shadow-lg shadow-logo-gold-600/30 transition-all">
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Create Destination'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
